/* eslint-disable react/prop-types */
import DatePicker from 'components/dashboards/manage-projects/DatePicker';
import React, { useState, useEffect } from 'react';
import DateRangeSelector from 'screens/common/components/DateRangeSelector';
import Select from 'react-select';

export const TableHeader = ({
  table,
  setShowColumnSelector,
  handleGlobalSearch,
  globalfilters = {
    search_text: '',
    start_date: '',
    end_date: '',
    dateRange: { start_date: '', end_date: '' }
  }, // Safe default
  dateRangePicker,
  globalSearch = true,
  dateFilter,
  showFilters = true,
  handleDateChange,
  dropdownFilter,
  dropdownData = [],
  dropdownOptions = {},
  selectedValues = {},
  setSelectedValues,
  clearAllDates
}) => {
  const [selectedRange, setSelectedRange] = useState({
    value: 'this_month',
    label: 'This Month'
  });

  // Local search state to handle typing properly
  const [searchValue, setSearchValue] = useState('');

  // Ensure globalfilters structure exists
  const safeGlobalFilters = {
    search_text: '',
    start_date: '',
    end_date: '',
    dateRange: {
      start_date: '',
      end_date: ''
    },
    ...globalfilters
  };

  // Update local search when global filters change
  useEffect(() => {
    setSearchValue(safeGlobalFilters.search_text || '');
  }, [safeGlobalFilters.search_text]);

  const parseDateToJSDate = dateStr => {
    // Check for null, undefined, or empty string
    if (!dateStr || dateStr === '' || dateStr.trim() === '') {
      return null;
    }

    if (typeof dateStr === 'string') {
      const parts = dateStr.split('-');
      if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
        const [year, month, day] = parts;
        const parsedDate = new Date(
          parseInt(year),
          parseInt(month, 10) - 1,
          parseInt(day)
        );
        return parsedDate;
      } else {
        return null;
      }
    }

    if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
      return dateStr;
    }
    return null;
  };

  const formatDateForDisplay = date => {
    if (!date) return '';
    const jsDate = date instanceof Date ? date : new Date(date);
    const day = String(jsDate.getDate()).padStart(2, '0');
    const month = String(jsDate.getMonth() + 1).padStart(2, '0');
    const year = jsDate.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleDateRangeChange = (date, field) => {
    // Ensure date is a Date object
    let processedDate = date;

    if (typeof date === 'string') {
      processedDate = new Date(date);
    }

    if (
      processedDate &&
      processedDate instanceof Date &&
      !isNaN(processedDate)
    ) {
      const year = processedDate.getFullYear();
      const month = String(processedDate.getMonth() + 1).padStart(2, '0');
      const day = String(processedDate.getDate()).padStart(2, '0');
      const formattedForQuery = `${year}-${month}-${day}`;

      if (handleDateChange) {
        handleDateChange(processedDate, field); // Pass the Date object
      }
    } else {
      if (handleDateChange) {
        handleDateChange(null, field);
      }
    }
  };

  // ===== IMPROVED CLEAR DATES HANDLER WITH SEQUENTIAL CLEARING =====
  const handleClearDates = async () => {
    if (clearAllDates) {
      clearAllDates();
    }
  };

  const handleSearchChange = value => {
    setSearchValue(value);
    if (handleGlobalSearch) {
      // Pass just the string value, not the event object
      handleGlobalSearch(value);
    }
  };

  // ===== GET CURRENT DATE VALUES =====
  const startDateValue = parseDateToJSDate(
    safeGlobalFilters.dateRange?.start_date || safeGlobalFilters.start_date
  );

  const endDateValue = parseDateToJSDate(
    safeGlobalFilters.dateRange?.end_date || safeGlobalFilters.end_date
  );

  // ===== CHECK IF ANY DATES ARE SELECTED =====
  const hasSelectedDates =
    (safeGlobalFilters.dateRange?.start_date &&
      safeGlobalFilters.dateRange.start_date !== '') ||
    (safeGlobalFilters.start_date && safeGlobalFilters.start_date !== '') ||
    (safeGlobalFilters.dateRange?.end_date &&
      safeGlobalFilters.dateRange.end_date !== '') ||
    (safeGlobalFilters.end_date && safeGlobalFilters.end_date !== '');

  const customStyles = {
    control: provided => ({
      ...provided,
      minHeight: '80px',
      backgroundColor: 'blue'
    }),
    valueContainer: provided => ({
      ...provided,
      whiteSpace: 'nowrap',
      overflowY: 'auto',
      textOverflow: 'ellipsis',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      maxWidth: '200px',
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      maxHeight: '90px'
    }),

    multiValue: provided => ({
      ...provided,
      backgroundColor: 'yellow',
      height: 'fit-content'
    }),
    multiValueLabel: provided => ({
      ...provided
    })
  };

  return (
    <div className="justify-content-end mb-1 flex-1 px-3">
      <div
        className={`d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 ${
          showFilters ? '' : 'd-none d-md-flex'
        }`}
      >
        <div className="d-flex flex-column flex-md-row gap-3">
          {globalSearch && (
            <input
              type="text"
              placeholder="Search..."
              className="form-control w-100"
              value={searchValue}
              onChange={e => handleSearchChange(e.target.value)}
            />
          )}
        </div>
        <div className="d-flex flex-column flex-md-row gap-3 w-100 w-md-auto">
          {dateRangePicker && (
            <DateRangeSelector
              selectedRange={selectedRange}
              setSelectedRange={setSelectedRange}
              handleDateChange={handleDateRangeChange}
              styleWidth={true}
            />
          )}
          <div className="datepicker-wrapper flex-grow-1 flex-md-shrink-0">
            {dateFilter && (
              <DatePicker
                selected={startDateValue}
                onChange={date => handleDateRangeChange(date, 'start_date')}
                placeholderText="Start Date"
                className="form-control"
                dateFormat="dd-MM-yyyy"
                maxDate={endDateValue || new Date()}
              />
            )}
          </div>

          <div className="datepicker-wrapper flex-grow-1 flex-md-shrink-0">
            {dateFilter && (
              <DatePicker
                selected={endDateValue}
                onChange={date => handleDateRangeChange(date, 'end_date')}
                placeholderText="End Date"
                className="form-control"
                dateFormat="dd-MM-yyyy"
                minDate={startDateValue}
                maxDate={new Date()}
              />
            )}
          </div>

          {dateFilter && hasSelectedDates && (
            <button
              type="button"
              className="btn btn-outline-secondary align-self-md-start"
              onClick={handleClearDates}
            >
              Clear
            </button>
          )}
          <div className="d-flex flex-wrap gap-3">
            {dropdownFilter &&
              dropdownData.map(drop => (
                <div
                  key={drop.key}
                  className="dropdown-filter flex-grow-1 flex-md-shrink-0"
                >
                  <Select
                    options={dropdownOptions[drop.key] || []}
                    isClearable={true}
                    placeholder={drop.label}
                    classNamePrefix="react-select"
                    value={
                      drop.isMulti
                        ? (selectedValues[drop.key] || [])
                            .map(v =>
                              dropdownOptions[drop.key]?.find(
                                opt => opt.value === v
                              )
                            )
                            .filter(Boolean)
                        : dropdownOptions[drop.key]?.find(
                            opt => opt.value === selectedValues[drop.key]
                          ) || null
                    }
                    onChange={selected => {
                      const selectedValuesData = drop.isMulti
                        ? selected
                          ? selected.map(item => item.value)
                          : []
                        : selected
                        ? selected.value
                        : null;

                      if (setSelectedValues) {
                        setSelectedValues(prev => ({
                          ...prev,
                          [drop.key]: selectedValuesData
                        }));
                      }
                    }}
                    isMulti={drop.isMulti}
                    closeMenuOnSelect={!drop.isMulti}
                    styles={{
                      ...customStyles,
                      control: base => ({
                        ...base,
                        minWidth: '200px',
                        maxWidth: '400px',
                        width: '100%'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        color: state.isDisabled ? '#d3d3d3' : '#000',
                        fontWeight: state.isDisabled ? 'lighter' : 'bold',
                        cursor: state.isDisabled ? 'not-allowed' : 'pointer'
                      })
                    }}
                  />
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};
